import React, { type FunctionComponent, useCallback, useState } from 'react'
import { css } from '@emotion/react'
import max from 'lodash/max'
import range from 'lodash/range'
import { colors } from '../../foundation/colors'
import { spacing } from '../../foundation/spacing'
import { Text } from '../text/Text'
import { ArrayTableColumnSelect } from './components/ArrayTableColumnSelect'
import { type ArrayTableMessages, type ArrayTableConfirmedColumn } from './utils/ArrayTable.types'

interface Props {
  /* Data displayed in table  */
  data: string[][]
  /* Enables selectable buttons on columns  */
  selectableColumns?: boolean
  /* Callback when select or deselect button is clicked*/
  onColumnSelect?: (selectedColumn?: number) => void
  /* Displays a message and disabled select functionality for the confirmed columns  */
  confirmedColumns?: ArrayTableConfirmedColumn[]
  /* Strings to display */
  messages?: ArrayTableMessages
}

const tableStyles = css({
  borderTop: `1px solid ${colors.GRAYSCALE.X20}`,
  tableLayout: 'fixed',
})

const selectionRowStyles = css({
  boxShadow: `0 1px ${colors.GRAYSCALE.X20}`,
  height: 40,
})
const rowStyles = css({ boxShadow: `0 1px ${colors.GRAYSCALE.X20}` })

const cellStyles = css({
  maxWidth: 360,
  paddingBottom: spacing.X12,
  paddingRight: spacing.X24,
  paddingTop: spacing.X12,
  textAlign: 'left',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
})

const bodyTextStyles = css({
  marginLeft: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

export const ArrayTable: FunctionComponent<Props> = ({
  data,
  selectableColumns,
  onColumnSelect,
  confirmedColumns,
  messages,
}) => {
  const [selectedColumn, setSelectedColumn] = useState<number>()

  const longestRowLength = max(data.map(row => row.length)) || 0

  const internalOnColumnSelect = useCallback(
    (columnIndex?: number) => {
      setSelectedColumn(columnIndex)
      onColumnSelect?.(columnIndex)
    },
    [setSelectedColumn, onColumnSelect]
  )

  const displaySelectionHeaders = selectableColumns || confirmedColumns

  return (
    <table css={tableStyles}>
      <thead>
        {displaySelectionHeaders && (
          <tr css={selectionRowStyles}>
            {range(longestRowLength).map((_, i) => (
              <th css={cellStyles} key={i}>
                <ArrayTableColumnSelect
                  selectable={selectableColumns}
                  columnIndex={i}
                  selectedColumn={selectedColumn}
                  onColumnSelect={internalOnColumnSelect}
                  confirmedColumn={confirmedColumns?.find(col => col.index === i)}
                  messages={messages}
                />
              </th>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr data-testid={`array-table-body-row-${i}`} css={rowStyles} key={i}>
            {row.map((cell, j) => (
              <td css={cellStyles} key={j}>
                <Text styles={bodyTextStyles}>{cell}</Text>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
